import type { ComponentType } from 'react';
import {
    UtensilsCrossed,
    ShoppingBag,
    PawPrint,
    Dumbbell,
    Waves,
    Moon,
    Landmark,
    Heart,
    CalendarDays,
    TreePalm,
} from 'lucide-react';

/** Chave estável: mesmo ícone Lucide da Home → paths SVG idênticos no mapa */
export type CategoryIconKey =
    | 'dumbbell'
    | 'utensils-crossed'
    | 'shopping-bag'
    | 'paw-print'
    | 'tree-palm'
    | 'waves'
    | 'moon'
    | 'landmark'
    | 'heart'
    | 'calendar-days';

export type CategoryPreset = {
    iconKey: CategoryIconKey;
    icon: ComponentType<{ size?: number }>;
    bg: string;
    color: string;
};

/** Corpos SVG (viewBox 24×24), traço branco — paths Lucide v0.556 */
const SVG_UTENSILS = `<path d="m16 2-2.3 2.3a3 3 0 0 0 0 4.2l1.8 1.8a3 3 0 0 0 4.2 0L22 8"/><path d="M15 15 3.3 3.3a4.2 4.2 0 0 0 0 6l7.3 7.3c.7.7 2 .7 2.8 0L15 15Zm0 0 7 7"/><path d="m2.1 21.8 6.4-6.3"/><path d="m19 5-7 7"/>`;
const SVG_PALM = `<path d="M13 8c0-2.76-2.46-5-5.5-5S2 5.24 2 8h2l1-1 1 1h4"/><path d="M13 7.14A5.82 5.82 0 0 1 16.5 6c3.04 0 5.5 2.24 5.5 5h-3l-1-1-1 1h-3"/><path d="M5.89 9.71c-2.15 2.15-2.3 5.47-.35 7.43l4.24-4.25.7-.7.71-.71 2.12-2.12c-1.95-1.96-5.27-1.8-7.42.35"/><path d="M11 15.5c.5 2.5-.17 4.5-1 6.5h4c2-5.5-.5-12-1-14"/>`;
const SVG_BAG = `<path d="M16 10a4 4 0 0 1-8 0"/><path d="M3.103 6.034h17.794"/><path d="M3.4 5.467a2 2 0 0 0-.4 1.2V20a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6.667a2 2 0 0 0-.4-1.2l-2-2.667A2 2 0 0 0 17 2H7a2 2 0 0 0-1.6.8z"/>`;
const SVG_WAVES = `<path d="M2 6c.6.5 1.2 1 2.5 1C7 7 7 5 9.5 5c2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/><path d="M2 12c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/><path d="M2 18c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/>`;
const SVG_PAW = `<circle cx="11" cy="4" r="2"/><circle cx="18" cy="8" r="2"/><circle cx="20" cy="16" r="2"/><path d="M9 10a5 5 0 0 1 5 5v3.5a3.5 3.5 0 0 1-6.84 1.045Q6.52 17.48 4.46 16.84A3.5 3.5 0 0 1 5.5 10Z"/>`;
const SVG_DUMBBELL = `<path d="M17.596 12.768a2 2 0 1 0 2.829-2.829l-1.768-1.767a2 2 0 0 0 2.828-2.829l-2.828-2.828a2 2 0 0 0-2.829 2.828l-1.767-1.768a2 2 0 1 0-2.829 2.829z"/><path d="m2.5 21.5 1.4-1.4"/><path d="m20.1 3.9 1.4-1.4"/><path d="M5.343 21.485a2 2 0 1 0 2.829-2.828l1.767 1.768a2 2 0 1 0 2.829-2.829l-6.364-6.364a2 2 0 1 0-2.829 2.829l1.768 1.767a2 2 0 0 0-2.828 2.829z"/><path d="m9.6 14.4 4.8-4.8"/>`;
const SVG_MOON = `<path d="M20.985 12.486a9 9 0 1 1-9.473-9.472c.405-.022.617.46.402.803a6 6 0 0 0 8.268 8.268c.344-.215.825-.004.803.401"/>`;
const SVG_LANDMARK = `<path d="M10 18v-7"/><path d="M11.12 2.198a2 2 0 0 1 1.76.006l7.866 3.847c.476.233.31.949-.22.949H3.474c-.53 0-.695-.716-.22-.949z"/><path d="M14 18v-7"/><path d="M18 18v-7"/><path d="M3 22h18"/><path d="M6 18v-7"/>`;
const SVG_HEART = `<path d="M2 9.5a5.5 5.5 0 0 1 9.591-3.676.56.56 0 0 0 .818 0A5.49 5.49 0 0 1 22 9.5c0 2.29-1.5 4-3 5.5l-5.492 5.313a2 2 0 0 1-3 .019L5 15c-1.5-1.5-3-3.2-3-5.5"/>`;
const SVG_CALENDAR = `<path d="M8 2v4"/><path d="M16 2v4"/><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M3 10h18"/><path d="M8 14h.01"/><path d="M12 14h.01"/><path d="M16 14h.01"/><path d="M8 18h.01"/><path d="M12 18h.01"/><path d="M16 18h.01"/>`;

export const CATEGORY_ICON_SVG: Record<CategoryIconKey, string> = {
    dumbbell: SVG_DUMBBELL,
    'utensils-crossed': SVG_UTENSILS,
    'shopping-bag': SVG_BAG,
    'paw-print': SVG_PAW,
    'tree-palm': SVG_PALM,
    waves: SVG_WAVES,
    moon: SVG_MOON,
    landmark: SVG_LANDMARK,
    heart: SVG_HEART,
    'calendar-days': SVG_CALENDAR,
};

/** Mesmos presets visuais da Home (ícone + cores pastel / accent) */
export const CATEGORY_PRESETS: Record<string, CategoryPreset> = {
    academias: { iconKey: 'dumbbell', icon: Dumbbell, bg: '#FEE2E2', color: '#B91C1C' },
    'comer-beber': { iconKey: 'utensils-crossed', icon: UtensilsCrossed, bg: '#FFEDD5', color: '#C2410C' },
    gastronomia: { iconKey: 'utensils-crossed', icon: UtensilsCrossed, bg: '#FFEDD5', color: '#C2410C' },
    restaurante: { iconKey: 'utensils-crossed', icon: UtensilsCrossed, bg: '#FFEDD5', color: '#C2410C' },
    compras: { iconKey: 'shopping-bag', icon: ShoppingBag, bg: '#FCE7F3', color: '#BE185D' },
    shopping: { iconKey: 'shopping-bag', icon: ShoppingBag, bg: '#FCE7F3', color: '#BE185D' },
    pet: { iconKey: 'paw-print', icon: PawPrint, bg: '#FEF3C7', color: '#B45309' },
    pets: { iconKey: 'paw-print', icon: PawPrint, bg: '#FEF3C7', color: '#B45309' },
    passeios: { iconKey: 'tree-palm', icon: TreePalm, bg: '#E0F2FE', color: '#0369A1' },
    praia: { iconKey: 'tree-palm', icon: TreePalm, bg: '#DBEAFE', color: '#1D4ED8' },
    natureza: { iconKey: 'tree-palm', icon: TreePalm, bg: '#DCFCE7', color: '#15803D' },
    atividades: { iconKey: 'waves', icon: Waves, bg: '#CCFBF1', color: '#0F766E' },
    'vida-noturna': { iconKey: 'moon', icon: Moon, bg: '#EDE9FE', color: '#5B21B6' },
    'pontos-turisticos': { iconKey: 'landmark', icon: Landmark, bg: '#D1FAE5', color: '#166534' },
    saude: { iconKey: 'heart', icon: Heart, bg: '#FFE4E6', color: '#BE123C' },
    eventos: { iconKey: 'calendar-days', icon: CalendarDays, bg: '#FEF9C3', color: '#A16207' },
};

export const CATEGORY_PRESET_DEFAULT: CategoryPreset = {
    iconKey: 'landmark',
    icon: Landmark,
    bg: '#ECFEFF',
    color: '#0E7490',
};

export function normalizeCategorySlug(slug: string): string {
    return slug.trim().toLowerCase().replace(/_/g, '-');
}

function presetFromLabel(label: string): CategoryPreset | null {
    const l = label.toLowerCase();
    if (/academia/.test(l)) return CATEGORY_PRESETS.academias;
    if (/(comer|beber|gastronom|restaur|aliment)/.test(l)) return CATEGORY_PRESETS['comer-beber'];
    if (/(compra|shopping|loja)/.test(l)) return CATEGORY_PRESETS.compras;
    if (/\bpet\b|pets?|animal/.test(l)) return CATEGORY_PRESETS.pet;
    if (/(passeio|turismo|praia|natureza)/.test(l)) return CATEGORY_PRESETS.passeios;
    if (/(noturn|noite|bar|balad)/.test(l)) return CATEGORY_PRESETS['vida-noturna'];
    if (/(ponto|turistic|monument|igreja|hist[oó]ric)/.test(l)) return CATEGORY_PRESETS['pontos-turisticos'];
    if (/(saúde|saude|bem estar|spa|clínica|clinica)/.test(l)) return CATEGORY_PRESETS.saude;
    if (/evento/.test(l)) return CATEGORY_PRESETS.eventos;
    if (/(atividad|esporte|lazer)/.test(l)) return CATEGORY_PRESETS.atividades;
    return null;
}

export function getCategoryPreset(slug: string, label: string): CategoryPreset {
    const n = normalizeCategorySlug(slug);
    if (n && CATEGORY_PRESETS[n]) return CATEGORY_PRESETS[n];
    const fromLabel = presetFromLabel(label);
    if (fromLabel) return fromLabel;
    return CATEGORY_PRESET_DEFAULT;
}

export function wrapCategoryPinSvg(paths: string): string {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths}</svg>`;
}

export function getCategoryPinMarkup(slug: string, label: string): { color: string; svg: string } {
    const preset = getCategoryPreset(slug, label);
    const paths = CATEGORY_ICON_SVG[preset.iconKey];
    return { color: preset.color, svg: wrapCategoryPinSvg(paths) };
}
